'use client'

import { useMemo, useState, type ReactNode } from 'react'
import { format } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { SiFacebook, SiInstagram, SiMessenger } from '@icons-pack/react-simple-icons'
import { IResearchedAd } from '@/api/research-jobs/ads/get-researched-ads-query'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { FUNNEL_STAGE_META } from '@/features/research/funnel-stage-meta'
import { IGrowthTimeframeFilterValue } from '@/features/research/research-ads-filters/state/research-ads-filters-state-types'
import { GrowthIndicator } from '@/features/research/research-ads/legacy/growth-indicator'
import {
    formatAdAttributeLabel,
    formatDisplayFormatLabel,
} from '@/features/research/research-ads/utils/ad-attribute-labels'
import {
    formatTargetsWithObservedLabel,
    getTargetingRowCopy,
} from '@/features/research/research-ads/utils/research-ad-targeting-copy'
import { cn } from '@/utils/styles/cn'

/**
 * One aligned label/value row — the Foreplay-style presentation the card and the preview modal
 * share: muted label in a fixed left column, medium value beside it. Callers skip absent values
 * entirely; a row never renders an empty value or a placeholder dash.
 */
export function AdDetailRow({
    label,
    children,
    valueClassName,
    className,
}: {
    label: ReactNode
    children: ReactNode
    /** Surface-specific value styling (the card right-aligns and sizes up; the modal stays default). */
    valueClassName?: string
    /** Wrapper overrides — narrow hosts swap the fixed 104px label column for an auto one. */
    className?: string
}) {
    return (
        <div className={cn('grid grid-cols-[104px_1fr] items-baseline gap-x-3', className)}>
            <span className="text-[12px] leading-normal text-muted-foreground">{label}</span>
            <span
                className={cn(
                    'flex min-w-0 items-center gap-1.5 break-words text-[13px] font-medium leading-normal',
                    valueClassName
                )}
            >
                {children}
            </span>
        </div>
    )
}

/** The card's value treatment: right edge, one step larger than the modal's rows. Exported for the
 *  Creative Performance card, which renders its KPIs through the same row so the two cannot drift. */
export const CARD_VALUE_CLASSES = 'justify-end text-right text-[14px]'

export function formatAdDate(value: string | Date) {
    return format(new Date(value), 'MMM d, yyyy')
}

export function getPlatformIcon(platform: string) {
    switch (platform) {
        case 'FACEBOOK':
            return <SiFacebook className="h-3.5 w-3.5" />
        case 'INSTAGRAM':
            return <SiInstagram className="h-3.5 w-3.5" />
        case 'MESSENGER':
            return <SiMessenger className="h-3.5 w-3.5" />
        default:
            return null
    }
}

/** "< N days" — Meta reports first/last seen, so the true running time is at most this. */
export function useRunningTimeLabel(ad: IResearchedAd) {
    return useMemo(() => {
        if (!ad.metrics.startDate) return '0 days'
        const start = new Date(ad.metrics.startDate)
        const end = ad.metrics.endDate ? new Date(ad.metrics.endDate) : new Date()
        const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
        return `< ${days} ${days === 1 ? 'day' : 'days'}`
    }, [ad.metrics.startDate, ad.metrics.endDate])
}

/**
 * The card's core KPI rows: reach (+growth), dates, running time, funnel stage, age range,
 * platforms — the same facts the old value-over-label grid carried, in the readable row form.
 */
/**
 * The card's targeting row. Label, caption and tooltip come from the copy helper keyed on the
 * basis, so a new basis (the country probe) brings its own wording instead of inheriting
 * "Targets". Absent codes render no row at all — the card never shows a negative state.
 */
export function AdTargetingRow({ ad }: { ad: IResearchedAd }) {
    const countries = formatTargetsWithObservedLabel(ad.metrics.targetCountryCodes ?? [], ad.metrics.countryProbe)
    const copy = getTargetingRowCopy(ad.metrics.targetingBasis ?? null)
    if (!countries || !copy) return null

    return (
        <AdDetailRow label={copy.label} valueClassName={CARD_VALUE_CLASSES}>
            <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                    <span className="cursor-help underline decoration-dotted underline-offset-2">
                        {countries.short}
                    </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-64">
                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-semibold">{countries.full}</span>
                        <p className="text-[11px] leading-snug text-muted-foreground">{copy.tooltip}</p>
                    </div>
                </TooltipContent>
            </Tooltip>
        </AdDetailRow>
    )
}

export function ResearchAdCoreDetailRows({
    ad,
    displayTimeframe,
    interactive = true,
}: {
    ad: IResearchedAd
    displayTimeframe: IGrowthTimeframeFilterValue
    /** When false the growth indicator is static — its chart needs org-scoped data. */
    interactive?: boolean
}) {
    const runningTime = useRunningTimeLabel(ad)
    const funnelStageMeta = ad.creative.funnelStage ? FUNNEL_STAGE_META[ad.creative.funnelStage] : undefined
    const platformIcons = ad.metrics.platforms.map((platform) => getPlatformIcon(platform)).filter(Boolean)

    const [showMore, setShowMore] = useState(false)
    // The captured Meta attributes stay behind a toggle: fully labeled rows read better than
    // truncation-prone chips, but always-on they'd tax every card's height for secondary facts.
    const cta = formatAdAttributeLabel(ad.creative.ctaType)
    const format = formatDisplayFormatLabel(ad.creative.displayFormat)
    const landing =
        ad.creative.linkUrl && ad.creative.linkDomain
            ? { url: ad.creative.linkUrl, domain: ad.creative.linkDomain }
            : null
    const hasMoreRows = Boolean(cta || format || landing)

    return (
        <div>
            {/* Static rows keep their own gap; the animated more-section lives outside it
                (gap on a parent of animating children leaves ghost spacing at height 0). */}
            <div className="flex flex-col gap-1.5">
            {ad.metrics.reachTotal > 0 && (
                <AdDetailRow label="Total Reach" valueClassName={CARD_VALUE_CLASSES}>
                    <span>{ad.metrics.reachTotal.toLocaleString()}</span>
                    <GrowthIndicator
                        adUuid={ad.uuid}
                        trendMetrics={ad.metrics.trendMetrics}
                        timeframe={displayTimeframe}
                        interactive={interactive}
                    />
                </AdDetailRow>
            )}
            {ad.metrics.startDate && (
                <AdDetailRow label="Start Date" valueClassName={CARD_VALUE_CLASSES}>
                    {formatAdDate(ad.metrics.startDate)}
                </AdDetailRow>
            )}
            <AdDetailRow label="Running Time" valueClassName={CARD_VALUE_CLASSES}>{runningTime}</AdDetailRow>
            {funnelStageMeta && (
                <AdDetailRow label="Funnel Stage" valueClassName={CARD_VALUE_CLASSES}>
                    <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                            <span className="cursor-help underline decoration-dotted underline-offset-2">
                                {funnelStageMeta.short}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-64">
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold">{funnelStageMeta.label}</span>
                                <p className="text-[11px] leading-snug text-muted-foreground">
                                    {funnelStageMeta.definition}
                                </p>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </AdDetailRow>
            )}
            {ad.metrics.ageMin && ad.metrics.ageMax && (
                <AdDetailRow label="Age Range" valueClassName={CARD_VALUE_CLASSES}>{`${ad.metrics.ageMin}-${ad.metrics.ageMax}`}</AdDetailRow>
            )}
            <AdTargetingRow ad={ad} />
            {platformIcons.length > 0 && (
                <AdDetailRow label="Platforms" valueClassName={CARD_VALUE_CLASSES}>
                    {ad.metrics.platforms.map((platform, index) => {
                        const icon = getPlatformIcon(platform)
                        if (!icon) return null
                        return (
                            <span key={index} className="flex items-center text-muted-foreground">
                                {icon}
                            </span>
                        )
                    })}
                </AdDetailRow>
            )}
            </div>

            {hasMoreRows && (
                <>
                    <AnimatePresence initial={false}>
                        {showMore && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{
                                    opacity: { duration: 0.15 },
                                    height: { type: 'spring', stiffness: 300, damping: 20, mass: 0.3 },
                                }}
                                className="overflow-hidden"
                            >
                                {/* Spacing on the inner child — the animating shell stays bare. */}
                                <div className="flex flex-col gap-1.5 pt-1.5">
                                    {cta && (
                                        <AdDetailRow label="CTA" valueClassName={CARD_VALUE_CLASSES}>
                                            {cta}
                                        </AdDetailRow>
                                    )}
                                    {format && (
                                        <AdDetailRow label="Format" valueClassName={CARD_VALUE_CLASSES}>
                                            {format}
                                        </AdDetailRow>
                                    )}
                                    {landing && (
                                        <AdDetailRow label="Landing Page" valueClassName={CARD_VALUE_CLASSES}>
                                            <a
                                                href={landing.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(event) => event.stopPropagation()}
                                                className="truncate underline decoration-stone-300 underline-offset-2 hover:decoration-stone-500"
                                            >
                                                {landing.domain}
                                            </a>
                                        </AdDetailRow>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation()
                            setShowMore((value) => !value)
                        }}
                        className="mt-1.5 flex items-center gap-1 text-[11px] text-stone-400 transition-colors hover:text-stone-600"
                    >
                        {showMore ? 'Less' : 'More details'}
                        <ChevronDown className={cn('h-3 w-3 transition-transform', showMore && 'rotate-180')} />
                    </button>
                </>
            )}
        </div>
    )
}
